import styles from 'css/src/exports.module.scss';
import selectionControlStyles from 'css/src/components/common/ui/SelectionControl.module.scss';

import type { InputHTMLAttributes } from 'react';
import React, { useCallback, useRef } from 'react';
import classNames from 'classnames';

import RadioCheckedIcon from 'js/src/libs/custom_icons/RadioCheckedIcon';
import RadioUncheckedIcon from 'js/src/libs/custom_icons/RadioUncheckedIcon';
import uuid from 'js/src/libs/uuid';

export type RadioInputValue = InputHTMLAttributes<HTMLInputElement>['value'] | boolean;

export type RadioInputProps = {
    name?: string;
    value?: RadioInputValue;
    text?: React.ReactNode;
    description?: React.ReactNode;
    checked?: boolean;
    readOnly?: boolean;
    disabled?: boolean;
    onChange?: (value: RadioInputValue) => void;
    id?: string;
    className?: string;
};

const RadioInputPresentation = ({
    name,
    value,
    onChange,
    text = '',
    description = '',
    checked = false,
    readOnly = false,
    disabled = false,
    id = `radio-${uuid()}`,
    className = '',
}: RadioInputProps) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const fill = disabled
        ? styles['allColors-color-grey-400']
        : styles['allColors-color-l5-primary-500'];
    const uncheckedFill = disabled
        ? styles['allColors-color-grey-400']
        : styles['allColors-color-grey-600'];

    const isChecked = checked;
    const isUnchecked = !checked;
    const isDisabled = disabled;
    const isReadOnly = readOnly;

    const handleChange = useCallback(() => {
        if (onChange) {
            onChange(value);
        }
    }, [onChange, value]);

    return (
        <label
            id={id}
            className={classNames(
                'radio d-flex align-items-start cursor-pointer',
                selectionControlStyles['selection-control'],
                {
                    'is-checked': isChecked,
                    'is-unchecked': isUnchecked,
                    'is-disabled': isDisabled,
                    'is-readonly': isReadOnly,
                    'pe-none': isDisabled || isReadOnly,
                },
                className,
            )}
        >
            <input
                name={name}
                value={typeof value === 'boolean' ? value.toString() : value}
                type="radio"
                checked={checked}
                readOnly={isReadOnly}
                disabled={isDisabled}
                onChange={handleChange}
                className="d-none"
                ref={inputRef}
            />
            <div
                className={classNames({
                    [selectionControlStyles['unchecked-icon']]: isUnchecked,
                })}
            >
                {isChecked && <RadioCheckedIcon fill={fill} />}
                {isUnchecked && <RadioUncheckedIcon fill={uncheckedFill} />}
            </div>
            {text && (
                <div className="ms-l5-8">
                    <div
                        className={classNames('font-l5 font-l5--small', {
                            'color-grey-900': !isDisabled,
                            'color-grey-400': isDisabled,
                        })}
                    >
                        {text}
                    </div>
                    {description && (
                        <div
                            className={classNames('font-l5 font-l5--small mt-l5-4', {
                                'color-grey-600': !isDisabled,
                                'color-grey-400': isDisabled,
                            })}
                        >
                            {description}
                        </div>
                    )}
                </div>
            )}
        </label>
    );
};

const RadioInput = React.memo(RadioInputPresentation);
export default RadioInput;
